import React, { useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useAccounts } from '../../context/AccountContext';
import ConnectAccountModal from '../../components/ConnectAccountModal';

// Manage the MT5 accounts connected to this user.
//
// Connect  → credentials stored, admin verifies, container starts.
// Disconnect → container removed; credentials, trade history and (paused)
//              relationships all kept, so reconnecting is one click.
// Delete   → only after disconnecting; removes the account and its history.

const STATUS_BADGE = {
  verified:     { cls: 'success', text: 'Connected' },
  pending:      { cls: 'warning', text: 'Awaiting verification' },
  disconnected: { cls: 'secondary', text: 'Disconnected' },
  suspended:    { cls: 'danger', text: 'Suspended' },
};

const CONTAINER_TEXT = {
  running: 'MT5 connected',
  starting: 'Starting…',
  awaiting_manual_login: 'Waiting for login',
  needs_attention: 'Needs attention',
  manual_login_timeout: 'Login timed out',
  auth_failed: 'Login rejected by broker',
  error: 'Error',
  stopped: 'Stopped',
  none: 'No container',
};

export default function Accounts() {
  const { user } = useAuth();
  const { accounts, loading, refresh, limit, used, canConnectMore, selectedId, select } = useAccounts();

  const [modal, setModal] = useState(null); // { account } | { account: null }
  const [busy, setBusy] = useState(null);   // account id being acted on
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(null);

  async function act(account, fn, okMsg) {
    setError(''); setMsg(''); setBusy(account.id);
    try {
      const res = await fn();
      setMsg(res?.data?.message || okMsg);
      await refresh();
      return res;
    } catch (err) {
      const data = err.response?.data;
      // Disconnecting with live mirrored positions comes back as a refusal
      // carrying the count — surface it as a confirm step, not a dead end.
      if (data?.code === 'open_positions') {
        setConfirmDisconnect({ account, openPositions: data.openPositions, message: data.error });
      } else {
        setError(data?.error || 'Action failed.');
      }
      return null;
    } finally {
      setBusy(null);
    }
  }

  const disconnect = (account, force = false) =>
    act(account, () => api.post(`/accounts/${account.id}/disconnect`, { force }),
      'MT5 account disconnected.');

  const reconnect = (account) =>
    act(account, () => api.post(`/accounts/${account.id}/reconnect`), 'Reconnecting…');

  const restart = (account) =>
    act(account, () => api.post(`/accounts/${account.id}/restart`), 'Container restarting…');

  async function remove(account) {
    if (!window.confirm(
      `Permanently delete "${account.displayLabel}"?\n\n`
      + 'Its trade history, copy relationships and performance data are deleted too. '
      + 'This cannot be undone.'
    )) return;
    const res = await act(account, () => api.delete(`/accounts/${account.id}`),
      'MT5 account deleted.');
    // The switcher must not stay pointed at an account that no longer exists.
    if (res && selectedId === account.id) select(null);
  }

  return (
    <div className="row">
      {modal && (
        <ConnectAccountModal
          account={modal.account}
          role={user?.role}
          onClose={() => setModal(null)}
          onSaved={async (data) => {
            setModal(null);
            setMsg(data?.message || 'Saved.');
            await refresh();
          }}
        />
      )}

      <div className="col-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h5 className="card-title mb-0">MT5 Accounts</h5>
              <small className="text-white-50">
                Each connected account runs its own MT5 terminal and copies
                independently. {limit ? `Using ${used} of ${limit}.` : ''}
              </small>
            </div>
            <button
              className="btn btn-primary btn-sm"
              disabled={!canConnectMore}
              title={canConnectMore ? '' : 'You have reached your account limit'}
              onClick={() => setModal({ account: null })}
            >
              + Connect account
            </button>
          </div>

          <div className="card-body">
            {msg && (
              <div className="alert alert-success alert-dismissible">{msg}
                <button className="close" onClick={() => setMsg('')}>&times;</button>
              </div>
            )}
            {error && (
              <div className="alert alert-danger alert-dismissible">{error}
                <button className="close" onClick={() => setError('')}>&times;</button>
              </div>
            )}

            {confirmDisconnect && (
              <div className="alert alert-warning">
                <p className="mb-2">{confirmDisconnect.message}</p>
                <button
                  className="btn btn-sm btn-danger mr-2"
                  disabled={busy === confirmDisconnect.account.id}
                  onClick={async () => {
                    const a = confirmDisconnect.account;
                    setConfirmDisconnect(null);
                    await disconnect(a, true);
                  }}
                >
                  Disconnect anyway — leave {confirmDisconnect.openPositions} position(s) open
                </button>
                <button
                  className="btn btn-sm btn-neutral"
                  onClick={() => setConfirmDisconnect(null)}
                >
                  Cancel
                </button>
              </div>
            )}

            {loading ? (
              <p className="text-muted">Loading…</p>
            ) : accounts.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted mb-3">
                  You haven't connected an MT5 account yet. Connect one to start
                  {user?.role === 'trader' ? ' broadcasting your trades.' : ' copying traders.'}
                </p>
                <button className="btn btn-primary" onClick={() => setModal({ account: null })}>
                  Connect your first account
                </button>
              </div>
            ) : (
              <div className="table-responsive table-cards table-cards-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Server</th>
                      <th>Status</th>
                      <th>MT5</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((a) => {
                      const badge = STATUS_BADGE[a.status] || STATUS_BADGE.pending;
                      const isBusy = busy === a.id;
                      return (
                        <tr key={a.id}>
                          <td data-label="Account">
                            <div>{a.displayLabel}</div>
                            <div className="text-muted small">
                              #{a.accountNumber}
                              {a.investorPassword ? ' · investor (read-only)' : ''}
                            </div>
                          </td>
                          <td data-label="Server" className="small">{a.server}</td>
                          <td data-label="Status">
                            <span className={`badge badge-${badge.cls}`}>{badge.text}</span>
                          </td>
                          <td data-label="MT5" className="small">
                            {a.status === 'verified'
                              ? (CONTAINER_TEXT[a.containerStatus] || a.containerStatus)
                              : '—'}
                            {a.containerError && (
                              <div className="text-danger small" style={{ maxWidth: 280 }}>
                                {a.containerError}
                              </div>
                            )}
                          </td>
                          <td data-label="Actions">
                            <button
                              className="btn btn-info btn-sm mr-1"
                              disabled={isBusy}
                              onClick={() => setModal({ account: a })}
                            >
                              Edit
                            </button>

                            {a.status === 'disconnected' ? (
                              <>
                                <button
                                  className="btn btn-success btn-sm mr-1"
                                  disabled={isBusy || !canConnectMore}
                                  title={canConnectMore ? '' : 'Account limit reached'}
                                  onClick={() => reconnect(a)}
                                >
                                  {isBusy ? '…' : 'Reconnect'}
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  disabled={isBusy}
                                  onClick={() => remove(a)}
                                >
                                  Delete
                                </button>
                              </>
                            ) : (
                              <>
                                {a.status === 'verified' && (
                                  <button
                                    className="btn btn-neutral btn-sm mr-1"
                                    disabled={isBusy}
                                    onClick={() => restart(a)}
                                  >
                                    Restart MT5
                                  </button>
                                )}
                                <button
                                  className="btn btn-warning btn-sm"
                                  disabled={isBusy}
                                  onClick={() => disconnect(a)}
                                >
                                  {isBusy ? '…' : 'Disconnect'}
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-white-50 small mb-0 mt-3">
              <strong>Disconnecting</strong> removes the account's MT5 terminal but keeps its
              credentials, trade history and copy settings — relationships are paused and
              resume when you reconnect. Close any open copied positions first: with the
              terminal gone, nothing can manage them.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
